import {
    useCallback, useContext, useRef, useState,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpClient } from '@s20/services/httpClient';
import { FormCircularProgress, FormContext, toasty } from '../common/forms/FormElements';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { getCompanyContactsOptions } from '../contact/Contact';
import { ApiRoutes } from '../Common';
import OrganizationContactForm from './OrganizationContactForm';

export const OrganizationContactSelect = ({
    companyId,
    name,
    disabled = false,
    value,
    allowContactTypeEdit = true,
    onSaveCallback,
    ...restProps
}) => {
    const [showContactForm, setShowContactForm] = useState({ show: false, readOnly: disabled });
    const contactFormRef = useRef(null);

    const handleClose = useCallback(() => setShowContactForm({ show: false, readOnly: false }), []);

    const context = useContext(FormContext);
    let fieldValue = value;

    if (context && context.values) {
        fieldValue = context.values[name];
    }

    const queryKey = ['organizationContacts', companyId];

    const { data, isLoading } = useQuery({
        queryKey,
        queryFn: () => getCompanyContactsOptions(companyId),
    });

    const { data: locationContactTypeData, isLoading: locationContactTypeDataLoading } = useQuery({
        queryKey: ['locationContactTypes'],
        queryFn: () => httpClient.js(ApiRoutes.locationContactType.all()),
    });

    const { data: selectedContactData } = useQuery({
        queryKey: ['contact', fieldValue],
        queryFn: () => fieldValue ? httpClient.js(ApiRoutes.locationContact.byId(fieldValue)) : {},
    });

    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: async (selectedContact) => {
            const isNew = parseInt(selectedContact.id ?? 0) <= 0;
            const result = await httpClient.andGetResponse(
                isNew ? httpClient.types.post : httpClient.types.put,
                isNew
                    ? ApiRoutes.locationContact.create()
                    : ApiRoutes.locationContact.update(selectedContact.id),
                { ...selectedContact, companyId },
                'Error Saving Contact',
            );

            if (Number.isInteger(result)) {
                return result;
            }

            return result.id;
        },
        onSuccess: (contactId) => {
            queryClient.invalidateQueries({ queryKey });
            queryClient.invalidateQueries({ queryKey: ['contact', fieldValue] });

            context?.setFieldValue(name, contactId);
            handleClose();

            toasty.success('Contact saved successfully');
        },
    });

    if (isLoading || locationContactTypeDataLoading) {
        return <FormCircularProgress />;
    }

    // For now, hard-code this ID
    const options = name === 'projectManagerId' ? data.projectManagerContactOptions : data.companyContactsOptions;

    return (
        <>
            <ValidatedSelect
                options={options}
                onAddButtonClick={() => setShowContactForm({ show: true, readOnly: false })}
                showEditButton={!!fieldValue}
                onEditButtonClick={() => setShowContactForm({ show: true, readOnly: disabled })}
                showViewButton={!!fieldValue}
                onViewButtonClick={() => setShowContactForm({ show: true, readOnly: true })}
                {...restProps}
                name={name}
            />

            <OrganizationContactForm
                id={`${name}_contactForm`}
                ref={contactFormRef}
                show={showContactForm.show}
                readOnly={showContactForm.readOnly}
                allowContactTypeEdit={allowContactTypeEdit}
                contact={selectedContactData || {}}
                onClose={handleClose}
                onSaveCallback={onSaveCallback ?? mutation.mutateAsync}
                locationContactTypes={locationContactTypeData}
                contactTypeAsPrimitive
            />
        </>
    );
};